// GR-3219 — the pieces of the day schedule that are logic rather than layout:
// what a chip says, what a day header says, and where the download points.
// Kept out of the component so they can be tested without booting FullCalendar,
// the same way calendarTooltip is.

// Monday-first, matching the server's beginning_of_week. FullCalendar hands us
// a Date whose UTC fields hold the calendar-timezone wall clock, so the maths
// stays in UTC to match the rendered slot.
export function weekStartOf(date) {
  const d = new Date(date.getTime());
  const shift = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - shift);
  return isoDate(d);
}

export function isoDate(date) {
  return date.toISOString().slice(0, 10);
}

// The chip answers "how many, where, when" at a glance. Everything else
// (the counted dietary breakdown, the requester's note, setup notes) lives in
// the pop-over: on the chip it made every card a different height and buried
// the three facts the grid is scanned for (design review, 28 Sep 2026).
//
// At most three lines, which is what the chip is sized for: covers and
// quantity share the first line when a booking carries both.
export function entryChipLines(props = {}) {
  const counts = [
    props.headcount != null && `${props.headcount} covers`,
    props.quantity != null && `Qty ${props.quantity}`,
  ].filter(Boolean);

  return [counts.join(" · "), props.venue, props.timeRange].filter(Boolean);
}

export function buildEntryChip(title, props) {
  const wrap = document.createElement("div");
  wrap.className = "fc-schedule-entry";

  const heading = document.createElement("div");
  heading.className = "fc-schedule-entry__title";
  heading.textContent = title;
  wrap.appendChild(heading);

  entryChipLines(props).forEach((text) => {
    const line = document.createElement("div");
    line.className = "fc-schedule-entry__meta";
    line.textContent = text;
    wrap.appendChild(line);
  });

  return wrap;
}

// A day nobody booked gets no total: "0 covers" is not information. Equipment
// and services carry no headcount, so those days report the booking count on
// its own rather than dressing a zero up as a number.
export function dayTotalLabel(totals) {
  if (!totals || !totals.bookings) return null;
  const bookings = `${totals.bookings} ${totals.bookings === 1 ? "booking" : "bookings"}`;
  if (totals.headcount > 0) return `${totals.headcount} covers · ${bookings}`;

  return bookings;
}

export function weekSummaryText(summary) {
  if (!summary) return null;

  const bookings = `${summary.bookings} ${summary.bookings === 1 ? "booking" : "bookings"} this week`;
  const peak = summary.peakHeadcount > 0 ? `, busiest day ${summary.peakHeadcount} covers` : "";

  return `${bookings}${peak}. Day totals count each booking once; they are not summed across the week.`;
}

// GR-3219 delta: `layout` picks the caterer's run sheet instead of the long
// format. It rides the same URL so the week on screen, the pinned facility
// type and the status filter carry over untouched. The statuses matter: the
// server treats a missing filter as every status, including the ones outside
// the legend that the timeline never shows.
export function downloadHref({ schedulePath, facilityType, weekStart, format, layout, statuses }) {
  const params = new URLSearchParams();
  if (facilityType) params.set("facility_type", facilityType);
  if (weekStart) params.set("week_start", weekStart);
  if (layout) params.set("layout", layout);
  (statuses || []).forEach((status) => params.append("statuses[]", status));
  const query = params.toString();

  return `${schedulePath}.${format}${query ? `?${query}` : ""}`;
}
